<template>
	<highcharts class="highcharts-container" :options="chartOptions" :highcharts="localHc" />
</template>

<script setup lang="ts">
import Highcharts, { Options, SeriesOptionsType } from 'highcharts';
import BaseChart from './BaseChartOptions';
import { computed } from 'vue';
import deepMerge from 'deepmerge';
import { Chart as highcharts } from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import { number } from '@filters/shared-filters';
import { useAppStore } from '@stores/app';
More(Highcharts);

const appStore = useAppStore();

const localHc = Highcharts;
const yAxisMax = 2500000;

const columnWidth = computed((): number => {
	if (appStore.isDesktop) {
		return 61;
	} else if (appStore.isTablet) {
		return 45;
	} else {
		return 24;
	}
});

const chartOptions = computed((): Options => {
	let localOptions: Options = {};

	const series: Array<SeriesOptionsType> = [
		{
			name: 'Investor growth',
			type: 'column',
			data: [
				{ name: '2015', y: 73035 },
				{ name: '2016', y: 124079 },
				{ name: '2017', y: 159583 },
				{ name: '2018', y: 272033 },
				{ name: '2019', y: 576833 },
				{ name: '2020', y: 796072 },
				{ name: '2021', y: 1167096 },
				{ name: '2022', y: 1840853 },
				{ name: '2023', y: 2110397 },
				{ name: '2024', y: 2339225 }
			],
			dataLabels: {
				enabled: true,
				useHTML: false,
				formatter(): string {
					const y = this.y;
					if (!y) return '';
					return appStore.isMobile ? formatAbbreviatedNumber(y) : number(y, 0, true);
				},
				style: {
					fontWeight: '600',
					fontSize: '14px',
					color: '#151513'
				}
			},
			color: '#517787'
		}
	];

	localOptions = {
		series,
		xAxis: {
			tickInterval: 1,
			type: 'category',
			labels: {
				rotation: appStore.isMobile ? -45 : 0,
				style: {
					fontSize: '14px',
					color: '#373633',
					fontWeight: '600'
				}
			}
		},
		yAxis: {
			title: {
				text: null
			},
			gridLineColor: '#D3D4D4',
			gridLineDashStyle: 'Dash',
			gridLineWidth: 1.25,
			labels: {
				style: {
					color: '#373633',
					fontSize: '14px',
					fontWeight: '600'
				},
				formatter(): string {
					const value = this.value;
					return formatAbbreviatedNumber(value);
				}
			},
			max: yAxisMax
		},
		plotOptions: {
			column: {
				pointWidth: columnWidth.value
			}
		}
	};

	return deepMerge(new BaseChart().getOptions(), localOptions);
});

function formatAbbreviatedNumber(value: number): string {
	if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
	if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
	return value.toString();
}
</script>
